/**
 * Protected route wrapper for sponsor-gated routes.
 * 
 * ACT 5: Uses explicit sponsorAccessState for deferred-payment access gate.
 * 
 * Security note: This is a UI guard only. Real access control
 * must be enforced server-side via sponsor_access validation.
 * 
 * Routes requiring sponsor_access:
 * - /discover
 * - /card/:id
 * - /intro-request
 */
import { Navigate, useLocation } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useHasSponsorAccess, useAuthStore } from '@/store/authStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Routes that require sponsor_access tier.
   * These routes should redirect to /paywall if access is not granted.
   */
  requiresSponsorAccess?: boolean;
}

/**
 * Guard for routes that require active sponsor access.
 *
 * ACT 5: Uses explicit sponsorAccessState (manual_access or paid_placeholder).
 * Falls back to /paywall if access is not granted.
 *
 * Friend account exception: Friends are allowed through regardless of
 * sponsor_access state, since they have different product entitlements
 * (they browse sponsors, they don't pay for access). Friend-specific UI
 * is handled by individual pages.
 *
 * CORRECTION04 (B-2.B): when auth bootstrap is in flight
 * (authStatus='loading' or 'unknown'), return null instead of
 * redirecting to /login. Without this guard, deep-link navigation
 * to a protected route races the bootstrap: the redirect fires on
 * the first render (isAuthenticated=false), sends the user to
 * /login → / → /discover after the bootstrap completes, and the
 * requested URL is lost. The bootstrap's getSession() is async
 * and the authStore still reports the cold-start state on first
 * render. We must wait for the bootstrap to settle before deciding.
 */
export function SponsorAccessRoute({ children, requiresSponsorAccess = false }: ProtectedRouteProps) {
  const hasAccess = useHasSponsorAccess();
  const { authStatus, isAuthenticated, registrationKind } = useAuthStore();
  const location = useLocation();

  // CORRECTION04: wait for auth bootstrap to settle before deciding.
  // The bootstrap calls /api/auth/me on mount; until it resolves
  // we cannot tell "anonymous" from "not yet known".
  if (authStatus === 'loading' || authStatus === 'unknown') {
    return null;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={RoutePaths.LOGIN} state={{ from: location }} replace />;
  }

  // Friend accounts bypass sponsor_access requirement
  // Friends browse sponsors and receive introductions without paying
  if (registrationKind === 'friend') {
    return <>{children}</>;
  }

  // Authenticated but requires sponsor_access - redirect to paywall
  if (requiresSponsorAccess && !hasAccess) {
    return <Navigate to={RoutePaths.PAYWALL} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Generic protected route wrapper.
 * Redirects to login if not authenticated.
 *
 * CORRECTION04 (B-2.B): when auth bootstrap is in flight
 * (authStatus='loading' or 'unknown'), return null instead of
 * redirecting to /login. The bootstrap's getSession() is async
 * and the authStore still reports the cold-start state on first
 * render. This matches the same guard applied to SponsorAccessRoute
 * and to RootEntry: never redirect while the source of truth is
 * still being read.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { authStatus, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Wait for auth bootstrap to settle before deciding.
  if (authStatus === 'loading' || authStatus === 'unknown') {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={RoutePaths.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
